import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Baseline, RemoveFormatting } from 'lucide-react';
import './RichText.css';

/* A note used to be plain text in a two-row textarea. Max, 2026-09-07: users want it taller and
   want to format it "như trong email" — bold, italic, a colour. So the field now holds a small
   fragment of html instead of a string.

   Two rules keep that from becoming a hole:

   1. Only the toolbar creates markup. A paste is inserted as plain text (execCommand('insertText')),
      so a copy out of Outlook cannot smuggle a <script>, a style block, or a 400-line Word wrapper
      into a field that later goes out as email. Formatting a pasted line is one more click; letting
      arbitrary html in is a permanent liability.
   2. The server sanitises the note again on save (taskNoteSanitize in server.js). What is stored is
      what other people are shown, and the api can be called without going near this component. */

/* Notes written before this existed are plain text and have to keep working, so the two are told
   apart by shape. An entity counts as much as a tag: the server's sanitiser turns a stray "<" into
   "&lt;", which leaves a perfectly ordinary note like "qty < 10" carrying no tag at all — read as
   plain text it would be escaped a second time and reach the screen as "qty &lt; 10". */
export const richIsHtml = (v) => /<(br|div|p|ul|ol|li|span|b|i|u|strong|em|s|a)\b|&(amp|lt|gt|quot|nbsp|#\d+);/i.test(String(v || ''));

const escHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* The stored value as html, for a preview, an email, or the editor itself. A legacy plain-text note
   is escaped and its newlines become <br>; a rich one is already html and passes through. */
export const richToHtml = (v) => {
  const s = String(v || '');
  if (!s) return '';
  return richIsHtml(s) ? s : escHtml(s).replace(/\r?\n/g, '<br>');
};

/* The same html, but with the list indent written inline, for the mail.

   A bare <ul> is at the mercy of whatever is reading it. The portal's own mail preview gives it no
   padding at all, so the bullets render outside the box and get clipped (Max, 2026-09-07: "khi chọn
   bullet list hoặc numbered list thì nó không tự thụt dòng vào"), and Outlook's Word engine is no
   more dependable. An inline padding is the one thing every client honours. It goes in FRONT of any
   attributes the tag already carries, because a duplicate attribute is decided by the first one. */
export const richToMailHtml = (v) => richToHtml(v).replace(
  /<(ul|ol)((?:"[^"]*"|'[^']*'|[^>"'])*)>/gi,
  (whole, tag, attrs) => `<${tag} style="margin:4px 0;padding-left:26px"${attrs || ''}>`);

/* The reverse, for the plain-text half of the email, the table cell that shows one truncated line,
   and the search haystack — none of which can render markup. A list item keeps its bullet so the
   text half of a mail still reads as a list. */
export const richToText = (v) => {
  const s = String(v || '');
  if (!s) return '';
  if (!richIsHtml(s)) return s;
  return s
    .replace(/<li\b[^>]*>/gi, '\n- ')
    // </li> is not in the list: <li> above already opened the line, and closing it too would put a
    // blank line between every bullet.
    .replace(/<\/(p|div|ul|ol)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// An empty editor still leaves <br> or an empty div behind, which must not count as a filled note.
export const richIsEmpty = (v) => !richToText(v).trim();

/* Gmail's palette is 80 swatches behind a dialog. A note needs enough colour to mark something
   urgent, not a colour picker — these are the ones people actually reach for. */
const COLOURS = [
  ['#202124', 'cDefault'], ['#D93025', 'cRed'], ['#E8710A', 'cOrange'], ['#188038', 'cGreen'],
  ['#1A73E8', 'cBlue'], ['#8430CE', 'cPurple'], ['#5F6368', 'cGrey'],
];

/* LINKS — only where a screen asks for them (`links`; the System Project description, Jay 30/10: "Text --> html format so
   that hyperlink can work"). A ticket note has none: it goes out in mail. An address typed or pasted becomes a link when
   the box is left (or when a saved text is shown), never while typing — rewriting the text under the caret moves it.
   A click on a link opens it in a new tab; the server keeps http(s) and mailto only. */
const URL_RE = /\b(https?:\/\/[^\s<>"']+)/g;
const linkify = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const hits = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.parentElement && n.parentElement.closest('a')) continue;
    URL_RE.lastIndex = 0;
    if (URL_RE.test(n.nodeValue)) hits.push(n);
  }
  hits.forEach((n) => {
    const frag = document.createDocumentFragment();
    let last = 0;
    n.nodeValue.replace(URL_RE, (m, url, at) => {
      const clean = url.replace(/[.,;:!?)\]]+$/, '');   // the full stop after an address is not part of it
      if (at > last) frag.appendChild(document.createTextNode(n.nodeValue.slice(last, at)));
      const a = document.createElement('a');
      a.href = clean; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.textContent = clean;
      frag.appendChild(a);
      last = at + clean.length;
      return m;
    });
    if (last < n.nodeValue.length) frag.appendChild(document.createTextNode(n.nodeValue.slice(last)));
    n.parentNode.replaceChild(frag, n);
  });
  return hits.length > 0;
};

export default function RichText({ value, onChange, placeholder = '', minHeight = 120, disabled = false, links = false }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [palette, setPalette] = useState(false);
  const [empty, setEmpty] = useState(true);

  /* innerHTML is only written when the incoming value is not already what the box holds. Writing it
     on every render puts the caret back at position zero after every keystroke. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = richToHtml(value);
    if (el.innerHTML !== next) { el.innerHTML = next; if (links) linkify(el); }
    setEmpty(richIsEmpty(value));
  }, [value, links]);

  /* Chrome's TYPING style is not ours to set. Empty the box with select-all + delete and it keeps
     the deleted text's colour for whatever is typed next — and it writes that colour as
     <font color>, a tag the server's allowlist drops. The text would survive the save and the colour
     would quietly not, which reads as the editor losing work. So a font tag is rewritten into the
     span the toolbar itself produces, before anything is stored. */
  const normalise = (html) => String(html)
    .replace(/<font\b([^>]*)>/gi, (whole, attrs) => {
      const m = /\bcolor\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs || '');
      const colour = m ? (m[2] ?? m[3] ?? m[4] ?? '').trim() : '';
      return colour && /^[#\w(),.%\s-]+$/.test(colour) ? `<span style="color: ${colour}">` : '<span>';
    })
    .replace(/<\/font\s*>/gi, '</span>');

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Chrome leaves "<br>" in a box the user has just emptied. Store nothing, not a stray tag.
    const html = el.innerHTML === '<br>' ? '' : normalise(el.innerHTML);
    setEmpty(richIsEmpty(html));
    onChange(html);
  }, [onChange]);

  const cmd = useCallback((name, arg) => {
    const el = ref.current;
    if (!el || disabled) return;
    el.focus();
    /* styleWithCSS decides what the browser emits, and the right answer differs per command.
       For a colour it must be ON, or Chrome writes <font color> — a tag Outlook and Gmail render
       inconsistently. For bold/italic/underline it must be OFF, so they come out as <b>/<i>/<u>:
       the oldest, most portable markup there is, and the pair styleWithCSS produces instead
       (a span carrying font-weight) is what was quietly losing the bold when a colour was applied
       over the same words afterwards. */
    try { document.execCommand('styleWithCSS', false, name === 'foreColor'); } catch { /* not fatal */ }
    document.execCommand(name, false, arg);
    emit();
  }, [emit, disabled]);

  /* Bold is the one command that is done by hand.

     Inside a ticket form Chrome's execCommand('bold') returns true and changes nothing — measured,
     not guessed: the same selection takes italic, underline, strikethrough, a colour and a list,
     and a contentEditable appended anywhere ABOVE the field wrapper bolds fine, so something in the
     rules around a field makes Chrome decide the command is already satisfied. Rather than keep
     guessing at which rule, the wrap is done directly, which also behaves the same in every browser.

     Un-bolding is an explicit font-weight rather than an unwrap: a selection that covers only part
     of a <b> cannot be unwrapped without splitting it, and an inline weight beats the tag. */
  const rangeIsBold = (range) => {
    const el = ref.current;
    const probe = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
    for (let n = probe; n && n !== el; n = n.parentElement) {
      const w = getComputedStyle(n).fontWeight;
      if (n.tagName === 'B' || n.tagName === 'STRONG') return true;
      if (Number(w) >= 600 || w === 'bold' || w === 'bolder') return true;
    }
    return false;
  };

  const toggleBold = useCallback(() => {
    const el = ref.current;
    if (!el || disabled) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed || !el.contains(range.commonAncestorContainer)) return;
    const off = rangeIsBold(range);
    const wrap = document.createElement(off ? 'span' : 'b');
    if (off) wrap.style.fontWeight = '400';
    const frag = range.extractContents();
    if (off) frag.querySelectorAll('b, strong').forEach((n) => n.replaceWith(...n.childNodes));
    wrap.appendChild(frag);
    range.insertNode(wrap);
    // Leave the words selected, so a second click is the way back.
    const back = document.createRange();
    back.selectNodeContents(wrap);
    sel.removeAllRanges();
    sel.addRange(back);
    emit();
  }, [emit, disabled]);

  const onBlur = () => { if (links && ref.current && !disabled) linkify(ref.current); emit(); };
  const onClick = (e) => {
    const a = links && e.target.closest && e.target.closest('a[href]');
    if (!a || !/^(https?:|mailto:)/i.test(a.getAttribute('href') || '')) return;
    e.preventDefault();
    window.open(a.getAttribute('href'), '_blank', 'noopener,noreferrer');
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
    emit();
  };

  // preventDefault on mousedown, or the click moves focus out of the box and takes the selection.
  const btn = (name, Icon, title, arg) => (
    <button type="button" className="rt-btn" title={title} disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => (name === 'bold' ? toggleBold() : cmd(name, arg))}>
      <Icon size={15} />
    </button>
  );

  return (
    <div className={`rt-wrap${disabled ? ' rt-disabled' : ''}`}>
      <div className="rt-bar">
        {btn('bold', Bold, t('rt.bold'))}
        {btn('italic', Italic, t('rt.italic'))}
        {btn('underline', Underline, t('rt.underline'))}
        {btn('strikeThrough', Strikethrough, t('rt.strike'))}
        <span className="rt-sep" />
        <div className="rt-colour">
          <button type="button" className="rt-btn" title={t('rt.colour')} disabled={disabled}
            onMouseDown={(e) => e.preventDefault()} onClick={() => setPalette((p) => !p)}>
            <Baseline size={15} />
          </button>
          {palette && (
            <div className="rt-swatches">
              {COLOURS.map(([hex, label]) => (
                <button key={hex} type="button" className="rt-swatch" title={t(`rt.${label}`)}
                  style={{ background: hex }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { cmd('foreColor', hex); setPalette(false); }} />
              ))}
            </div>
          )}
        </div>
        <span className="rt-sep" />
        {btn('insertUnorderedList', List, t('rt.bullets'))}
        {btn('insertOrderedList', ListOrdered, t('rt.numbers'))}
        <span className="rt-sep" />
        {btn('removeFormat', RemoveFormatting, t('rt.clear'))}
      </div>
      <div className="rt-box-wrap">
        {empty && !!placeholder && <div className="rt-ph">{placeholder}</div>}
        <div
          ref={ref}
          className="rt-box"
          style={{ minHeight }}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onBlur={onBlur}
          onClick={onClick}
          onPaste={onPaste}
        />
      </div>
    </div>
  );
}
